import { useCallback } from 'react';
import { Stack } from '@mantine/core';
import InboxFilterBar from '@components/InboxReviews/InboxFilterBar';
import InboxSkeletonLoader from '@components/InboxReviews/InboxSkeletonLoader';
import JobMessagesTable from '@components/InboxReviews/Tables/JobMessagesTable';
import ThPagination from '@components/ThPagination/ThPagination';
import {
  useWorkerJobMessages,
  useMarkJobChatAsRead,
  useMarkJobChatAsUnread,
} from '@worker/hooks/useWorkerInbox';
import { useWorkerInboxQueryState } from '@worker/hooks/useWorkerInboxQueryState';
import { useChatStore } from '@stores/chatStore';
import { useUserContext } from '@state/userContext';
import type { JobChat } from '@th-types/inbox-reviews.type';
import { InboxChatModeEnum } from '@th-types/inbox-reviews.type';
import { ChatType, ChatEntityType } from '@th-types/chat.type';
import { useTableSelection } from '@hooks/useTableSelection';
import { getJobChatId } from '@components/InboxReviews/inboxSelectionHelpers';
import {
  InboxSearchParams,
  handleInboxClear,
  handleInboxPageChange,
  handleInboxSearch,
  handleInboxToggleUnread,
  handleInboxChatModeChange,
} from '@utils/InboxHelper';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';
import { buildJobViewUrl } from '@worker/helpers/my-jobs-helpers';

export default function JobMessagesTab() {
  const { searchParams, setSearchParams, urlParams } =
    useWorkerInboxQueryState();

  const currentOffset = urlParams.offset ?? 0;
  const showOnlyUnread = urlParams.showOnlyNew ?? false;
  const chatMode = (urlParams.mode as InboxChatModeEnum) ?? null;

  const openChat = useChatStore((state) => state.openChat);
  const { id: currentUserId } = useUserContext();
  const { mutate: markAsRead } = useMarkJobChatAsRead();
  const { mutate: markAsUnread } = useMarkJobChatAsUnread();
  const selection = useTableSelection<JobChat>(getJobChatId);

  const handleToggleRead = useCallback(
    (chat: JobChat) => {
      if (chat.unreadMessages > 0) {
        markAsRead(chat.chatId);
      } else {
        markAsUnread(chat.chatId);
      }
    },
    [markAsRead, markAsUnread]
  );

  const handleOpenChat = useCallback(
    (chat: JobChat) => {
      if (chat.unreadMessages > 0) {
        markAsRead(chat.chatId);
      }
      openChat(
        {
          chatType: ChatType.JOB,
          jobAssignmentId: chat.chatId,
          unreadMessages: chat.unreadMessages,
          firstUnreadMessageId: chat.firstUnreadMessageId ?? null,
          entity: {
            id: chat.memberId,
            type: ChatEntityType.COMPANY,
            name: chat.companyName,
            profileImageUrl: chat.companyLogoUrl,
          },
          jobInfo: {
            jobId: chat.jobAssignmentInfo.jobId,
            eventName: chat.jobAssignmentInfo.eventName,
            jobPosition: chat.jobAssignmentInfo.position,
            startDate: chat.jobAssignmentInfo.start,
            endDate: chat.jobAssignmentInfo.end,
            jobDetailUrl:
              buildJobViewUrl(chat.jobAssignmentId, null) ?? undefined,
          },
        },
        currentUserId
      );
    },
    [openChat, currentUserId, markAsRead]
  );

  const { data, isLoading } = useWorkerJobMessages(
    currentOffset,
    DEFAULT_ITEMS_PER_PAGE,
    urlParams.searchState?.searchText,
    urlParams.searchState?.dateRange,
    showOnlyUnread || undefined,
    chatMode ?? undefined
  );

  const handleSearch = useCallback(
    (search: InboxSearchParams) => {
      handleInboxSearch(search, searchParams, setSearchParams, {
        itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      });
    },
    [searchParams, setSearchParams]
  );

  const handleClear = useCallback(() => {
    handleInboxClear(setSearchParams, {
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    });
  }, [setSearchParams]);

  /**
   * Bulk mark handlers fire one mutation per chat.
   * Replace with a single bulk endpoint call when BE supports it.
   */
  const handleMarkAllAsRead = useCallback(() => {
    const unreadChats = (data?.jobChats ?? []).filter(
      (chat) => chat.unreadMessages > 0
    );
    unreadChats.forEach((chat) => markAsRead(chat.chatId));
  }, [data, markAsRead]);

  const handleMarkAllAsUnread = useCallback(() => {
    const readChats = (data?.jobChats ?? []).filter(
      (chat) => chat.unreadMessages === 0
    );
    readChats.forEach((chat) => markAsUnread(chat.chatId));
  }, [data, markAsUnread]);

  const handleMarkSelectionAsRead = useCallback(() => {
    const selected = (data?.jobChats ?? []).filter((chat) =>
      selection.selectedIds.has(getJobChatId(chat))
    );
    selected.forEach((chat) => markAsRead(chat.chatId));
  }, [data, selection, markAsRead]);

  const handleMarkSelectionAsUnread = useCallback(() => {
    const selected = (data?.jobChats ?? []).filter((chat) =>
      selection.selectedIds.has(getJobChatId(chat))
    );
    selected.forEach((chat) => markAsUnread(chat.chatId));
  }, [data, selection, markAsUnread]);

  const jobMessages = data?.jobChats ?? [];

  return (
    <Stack gap="md" w="100%" p={{ base: 'xs', sm: 'md' }}>
      <InboxFilterBar
        onSearch={handleSearch}
        onClear={handleClear}
        onToggleUnread={() =>
          handleInboxToggleUnread(showOnlyUnread, searchParams, setSearchParams)
        }
        showOnlyUnread={showOnlyUnread}
        chatMode={chatMode}
        onChatModeChange={(mode) =>
          handleInboxChatModeChange(mode, searchParams, setSearchParams)
        }
        onMarkAllAsRead={handleMarkAllAsRead}
        onMarkAllAsUnread={handleMarkAllAsUnread}
        selectedCount={selection.selectedCount}
        onMarkSelectionAsRead={handleMarkSelectionAsRead}
        onMarkSelectionAsUnread={handleMarkSelectionAsUnread}
      />

      {isLoading ? (
        <InboxSkeletonLoader />
      ) : (
        <>
          <JobMessagesTable
            jobMessages={jobMessages}
            onOpenChat={handleOpenChat}
            onToggleRead={handleToggleRead}
            selectionProps={selection.getSelectionProps(jobMessages)}
            getCompanyHref={(chat) =>
              chat.profileURLName
                ? `${import.meta.env.VITE_SERVER_BASE_URL}/companies/${chat.profileURLName}`
                : undefined
            }
            getEventHref={(chat) =>
              buildJobViewUrl(chat.jobAssignmentId, null) ?? undefined
            }
          />
          {data?.pagination &&
            data.pagination.count > DEFAULT_ITEMS_PER_PAGE && (
              <ThPagination
                currentOffset={currentOffset}
                itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
                totalItems={data.pagination.count}
                onPageChange={(offset) =>
                  handleInboxPageChange(offset, searchParams, setSearchParams)
                }
              />
            )}
        </>
      )}
    </Stack>
  );
}
